import React, { useState } from 'react';
import { Flex, Input, Select, Tag, Button } from 'antd';
import { SearchFilter } from 'src/usecases/messageRoom/reader';

type Props = {
  placeholder: string;
  onSearch: (value: string) => void;
  children?: React.ReactNode;
  isLoading?: boolean;
};

export const SearchBox: React.FC<Props> = ({
  placeholder,
  onSearch,
  children,
  isLoading,
}) => {
  return (
    <Flex
      style={{
        background: 'white',
        borderRadius: 3,
        padding: '2% 0 1% 2%',
        overflow: 'auto',
      }}
      vertical
      align="flex-start"
    >
      <h3 style={{ padding: '0%' }}>検索</h3>
      <div style={{ height: '10px' }}></div>
      <Flex style={{ width: '30%' }}>
        <Input.Search
          allowClear
          placeholder={placeholder}
          enterButton="検索"
          size="middle"
          loading={isLoading}
          style={{ paddingRight: '1%' }}
          onSearch={onSearch}
        />
      </Flex>
      <div style={{ padding: '1% 1% 0 0' }}>{children}</div>
    </Flex>
  );
};

type PropsSearchWithDropDown = {
  onFiltersChange: (filters: SearchFilter[]) => void;
  children?: React.ReactNode;
  isLoading?: boolean;
  // 既存のお気に入りフィルタ用
  options: any;
  defaultValue: string;
  handleChange: (value: string) => void;
  searchResultCount?: number;
};

// フィルター項目の選択肢（エクスポートして他のコンポーネントでも使用可能）
export const filterOptions = [
  { value: 'mCusId', label: '顧客ID' },
  { value: 'mCusName', label: '顧客名' },
  { value: 'nickname', label: 'ニックネーム' }
];

export const SearchBoxWithDropdownBox: React.FC<PropsSearchWithDropDown> = ({
  onFiltersChange,
  children,
  isLoading,
  options,
  defaultValue,
  handleChange,
  searchResultCount,
}) => {
  const [filters, setFilters] = useState<SearchFilter[]>([]);
  const [selectedField, setSelectedField] = useState<string>('mCusId');
  const [inputValue, setInputValue] = useState<string>('');

  // フィルター追加
  const handleAddFilter = () => {
    if (!inputValue.trim()) return;

    const newFilter: SearchFilter = {
      id: crypto.randomUUID(),
      field: selectedField as SearchFilter['field'],
      label: filterOptions.find(opt => opt.value === selectedField)?.label || '',
      value: inputValue.trim(),
    };

    const updatedFilters = [...filters, newFilter];
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);

    // 入力値をリセット
    setInputValue('');
  };

  // フィルター削除
  const handleRemoveFilter = (filterId: string) => {
    const updatedFilters = filters.filter(filter => filter.id !== filterId);
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  // 全フィルタークリア
  const handleClearAllFilters = () => {
    setFilters([]);
    onFiltersChange([]);
  };

  // エンターキーでフィルター追加
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddFilter();
    }
  };

  return (
    <Flex
      style={{
        background: 'white',
        borderRadius: 3,
        padding: '2% 0 1% 2%',
        overflow: 'auto',
      }}
      vertical
      align="flex-start"
    >
      <h3 style={{ padding: '0%' }}>検索</h3>
      <div style={{ height: '10px' }}></div>

      <Flex style={{ width: '100%', marginBottom: '16px' }} wrap="wrap" gap="small" align="center">
        <Select
          value={selectedField}
          style={{ width: 150 }}
          options={filterOptions}
          onChange={setSelectedField}
        />
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          style={{ width: 200 }}
        />
        <Button
          type="primary"
          onClick={handleAddFilter}
          disabled={!inputValue.trim()}
          loading={isLoading}
        >
          検索
        </Button>

        {filters.length > 0 && (
          <Button
            type="text"
            size="small"
            onClick={handleClearAllFilters}
            style={{ color: '#999', marginLeft: '16px' }}
          >
            全てクリア
          </Button>
        )}
      </Flex>

      {filters.length > 0 && (
          <Flex wrap="wrap" gap="small" style={{ marginBottom: '16px', width: '100%' }}>
            {filters.map(filter => (
              <Tag
                key={filter.id}
                closable
                onClose={() => handleRemoveFilter(filter.id)}
                color="blue"
                style={{
                  fontSize: '13px',
                  padding: '4px 8px',
                  borderRadius: '6px'
                }}
              >
                {filter.label}: "{filter.value}"
              </Tag>
            ))}
          </Flex>
      )}

      <Flex style={{ width: '100%', marginBottom: '16px' }}>
        <Select 
          showSearch={false}
          defaultValue={defaultValue}
          style={{ minWidth: "200px" }}
          options={options}
          onChange={handleChange}
        />
      </Flex>

      {filters.length > 0 && searchResultCount !== undefined && (
        <div style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
          表示中: {searchResultCount}件
        </div>
      )}

      <div style={{ padding: '1% 1% 0 0', width: '100%' }}>{children}</div>
    </Flex>
  );
};
